/**
 * Monta MedicationDispense R5 para POST EHRPoster.
 * recorded = emissão (agora); validUntil = extension valueDate (não whenHandedOver/statusChanged).
 * @module purchase-release/buildMedicationDispenseDraft
 */

import type { Bundle, MedicationDispense } from "fhir/r5";
import dayjs from "dayjs";
import { oids } from "@/configs/Settings";

import {
  PURCHASE_RELEASE_BATCH_EXTENSION_URL,
  PURCHASE_RELEASE_TRANSACTION_ID_EXTENSION_URL,
  PURCHASE_RELEASE_VALID_UNTIL_EXTENSION_URL,
  WOO_PRODUCT_CODE_SYSTEM,
} from "./constants";
import type {
  BuildPurchaseReleaseBatchParams,
  LocalPurchaseReleaseBatch,
  PurchaseReleaseLineInput,
} from "./types";

function newLocalId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function toIsoDateStart(dateYmd: string): string {
  // whenPrepared opcional: espelho do dia da liberação (sort) — não substitui recorded/extension
  return dayjs(dateYmd).startOf("day").toISOString();
}

/**
 * Recurso pronto para POST (sem id local).
 * validUntil = extension PURCHASE_RELEASE_VALID_UNTIL; recorded = emissão.
 */
export function buildMedicationDispenseResource(
  line: PurchaseReleaseLineInput,
  params: BuildPurchaseReleaseBatchParams,
  batchId: string,
): MedicationDispense {
  const orgId = params.organizationId.trim();
  const orgRef = `Organization/${oids.cnpj}-${orgId}`;
  const roleId = params.practitionerRoleId?.trim();

  const performers: MedicationDispense["performer"] = [
    {
      actor: {
        reference: orgRef,
        display: params.storeName,
      },
    },
  ];
  if (roleId) {
    performers.push({
      actor: { reference: `PractitionerRole/${roleId}` },
    });
  }

  const supportingInformation = params.supportingDocumentIds
    .filter(Boolean)
    .map((id) => ({ reference: `DocumentReference/${id}` }));

  // recorded = data/hora da emissão da liberação (obrigatório nesta entrega)
  const recorded = dayjs().toISOString();

  return {
    resourceType: "MedicationDispense",
    status: "completed",
    medication: {
      concept: {
        coding: [
          {
            system: WOO_PRODUCT_CODE_SYSTEM,
            code: String(line.product.id),
            display: line.product.name,
          },
        ],
        text: line.product.name,
      },
    },
    subject: { reference: params.patientReference },
    recorded,
    whenPrepared: toIsoDateStart(line.releasedAt || dayjs().format("YYYY-MM-DD")),
    quantity: {
      value: line.quantity,
      unit: "unidade",
      system: "http://unitsofmeasure.org",
      code: "1",
    },
    performer: performers,
    ...(supportingInformation.length > 0 && { supportingInformation }),
    extension: [
      {
        // Validade comercial da liberação (não usar whenHandedOver nem statusChanged)
        url: PURCHASE_RELEASE_VALID_UNTIL_EXTENSION_URL,
        valueDate: line.validUntil,
      },
      {
        url: PURCHASE_RELEASE_BATCH_EXTENSION_URL,
        valueString: batchId,
      },
      ...(params.transactionId?.trim()
        ? [
            {
              url: PURCHASE_RELEASE_TRANSACTION_ID_EXTENSION_URL,
              valueString: params.transactionId.trim(),
            },
          ]
        : []),
    ],
    note: [
      {
        text: `Liberação de compra — loja ${params.storeName} (produto Woo #${line.product.id})`,
      },
    ],
  };
}

/** Bundle transaction EHRPoster: N× POST MedicationDispense. */
export function buildMedicationDispenseTransactionBundle(
  params: BuildPurchaseReleaseBatchParams,
  batchId: string,
): Bundle {
  const entry = params.lines.map((line) => ({
    resource: buildMedicationDispenseResource(line, params, batchId),
    request: { method: "POST" as const, url: "MedicationDispense" },
  }));

  return {
    resourceType: "Bundle",
    type: "transaction",
    entry,
  };
}

/**
 * Monta lote local (cache UI) a partir dos recursos — ids reais após o POST.
 */
export function buildLocalPurchaseReleaseBatch(
  params: BuildPurchaseReleaseBatchParams,
  persistedDispenses?: MedicationDispense[],
): LocalPurchaseReleaseBatch {
  if (!params.lines.length) {
    throw new Error("Inclua ao menos um produto na liberação.");
  }

  const batchId = newLocalId("batch");
  const dispenses =
    persistedDispenses?.length === params.lines.length
      ? persistedDispenses
      : params.lines.map((line) =>
          buildMedicationDispenseResource(line, params, batchId),
        );

  // Garante batch-id consistente nos recursos montados localmente
  const withBatch = dispenses.map((d) => {
    const ext = [...(d.extension ?? [])];
    const idx = ext.findIndex(
      (e) => e.url === PURCHASE_RELEASE_BATCH_EXTENSION_URL,
    );
    if (idx >= 0) {
      ext[idx] = { ...ext[idx], valueString: batchId };
    } else {
      ext.push({
        url: PURCHASE_RELEASE_BATCH_EXTENSION_URL,
        valueString: batchId,
      });
    }
    return { ...d, extension: ext };
  });

  return {
    batchId,
    patientKey: params.patientReference,
    organizationId: params.organizationId,
    storeId: params.storeId,
    storeName: params.storeName,
    createdAt: dayjs().toISOString(),
    dispenses: withBatch,
  };
}

export function createEmptyReleaseLine(
  product: PurchaseReleaseLineInput["product"],
  releasedAt?: string,
  validUntil?: string,
): PurchaseReleaseLineInput {
  const releaseDate = releasedAt || dayjs().format("YYYY-MM-DD");
  return {
    localId: newLocalId("line"),
    product,
    quantity: 1,
    releasedAt: releaseDate,
    validUntil:
      validUntil || dayjs(releaseDate).add(90, "day").format("YYYY-MM-DD"),
  };
}
